import { useEffect, useState } from 'react';
import * as attorneyGeneralService from '../../../services/attorneyGeneralCaseRequests';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import { courtTypeQueryParam } from './attorneyGeneralHelpers';

export type AttorneyGeneralLookupScope =
  | 'step1'
  | 'step2'
  | 'step3'
  | 'step4'
  | 'step5'
  | 'step6'
  | 'step7'
  | 'step8'
  | 'step9'
  | 'step10'
  | 'step11'
  | 'step12'
  | 'review';

type LookupBundle = {
  provinces: LookupItem[];
  districts: LookupItem[];
  magistrateCourts: LookupItem[];
  highCourts: LookupItem[];
  policeStations: LookupItem[];
  countries: LookupItem[];
  courtTypes: LookupItem[];
  courts: LookupItem[];
  urgencyLevels: LookupItem[];
  deliveryMethods: LookupItem[];
  nextSteps: LookupItem[];
  genders: LookupItem[];
  suspectStatuses: LookupItem[];
  custodyStatuses: LookupItem[];
  nationalities: LookupItem[];
  preferredMethodOfAccess: LookupItem[];
  preferredLanguages: LookupItem[];
  respondentTypes: LookupItem[];
};

const EMPTY: LookupBundle = {
  provinces: [],
  districts: [],
  magistrateCourts: [],
  highCourts: [],
  policeStations: [],
  countries: [],
  courtTypes: [],
  courts: [],
  urgencyLevels: [],
  deliveryMethods: [],
  nextSteps: [],
  genders: [],
  suspectStatuses: [],
  custodyStatuses: [],
  nationalities: [],
  preferredMethodOfAccess: [],
  preferredLanguages: [],
  respondentTypes: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

const STATIC_SCOPES = new Set<AttorneyGeneralLookupScope>(['step2', 'step7', 'step12']);

export function useAttorneyGeneralLookups(
  scope: AttorneyGeneralLookupScope,
  provinceId?: string,
  districtId?: string,
  courtTypeId?: string,
) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(!STATIC_SCOPES.has(scope));
  const [courtsLoading, setCourtsLoading] = useState(false);

  useEffect(() => {
    if (STATIC_SCOPES.has(scope)) {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    const load = async (): Promise<Partial<LookupBundle>> => {
      if (scope === 'step11') {
        const del = await attorneyGeneralService.getDeliveryMethods();
        return { deliveryMethods: del.success ? del.data : [] };
      }
      if (scope === 'step1') {
        const [prov, courtTypes, countries] = await Promise.all([
          attorneyGeneralService.getProvinces(),
          attorneyGeneralService.getAttorneyGeneralCourtTypes(),
          attorneyGeneralService.getCountries(),
        ]);
        return {
          provinces: prov.success ? prov.data : [],
          courtTypes: courtTypes.success ? courtTypes.data : [],
          countries: countries.success ? countries.data : [],
        };
      }
      if (scope === 'step3') {
        const next = await attorneyGeneralService.getAttorneyGeneralNextSteps();
        return { nextSteps: next.success ? next.data : [] };
      }
      if (scope === 'step4' || scope === 'step6') {
        const [genders, countries, custody, nationalities] = await Promise.all([
          attorneyGeneralService.getGenders(),
          attorneyGeneralService.getCountries(),
          scope === 'step6' ? attorneyGeneralService.getCustodyStatuses() : Promise.resolve({ success: true, data: [] as LookupItem[] }),
          scope === 'step6' ? attorneyGeneralService.getNationalities() : Promise.resolve({ success: true, data: [] as LookupItem[] }),
        ]);
        return {
          genders: genders.success ? genders.data : [],
          countries: countries.success ? countries.data : [],
          custodyStatuses: custody.success ? custody.data : [],
          nationalities: nationalities.success ? nationalities.data : [],
        };
      }
      if (scope === 'step5') {
        const statuses = await attorneyGeneralService.getSuspectStatuses();
        return { suspectStatuses: statuses.success ? statuses.data : [] };
      }
      if (scope === 'step9') {
        const [access, languages] = await Promise.all([
          attorneyGeneralService.getPreferredMethodOfAccess(),
          attorneyGeneralService.getPreferredLanguages(),
        ]);
        return {
          preferredMethodOfAccess: access.success ? access.data : [],
          preferredLanguages: languages.success ? languages.data : [],
        };
      }
      if (scope === 'step8') {
        const types = await attorneyGeneralService.getRespondentTypes();
        return { respondentTypes: types.success ? types.data : [] };
      }
      if (scope === 'step10') {
        const urg = await attorneyGeneralService.getUrgencyLevels();
        return { urgencyLevels: urg.success ? urg.data : [] };
      }

      const [prov, urg, del, courtTypes, next, countries, genders, statuses, custody, nationalities, access, languages, types] =
        await Promise.all([
          attorneyGeneralService.getProvinces(),
          attorneyGeneralService.getUrgencyLevels(),
          attorneyGeneralService.getDeliveryMethods(),
          attorneyGeneralService.getAttorneyGeneralCourtTypes(),
          attorneyGeneralService.getAttorneyGeneralNextSteps(),
          attorneyGeneralService.getCountries(),
          attorneyGeneralService.getGenders(),
          attorneyGeneralService.getSuspectStatuses(),
          attorneyGeneralService.getCustodyStatuses(),
          attorneyGeneralService.getNationalities(),
          attorneyGeneralService.getPreferredMethodOfAccess(),
          attorneyGeneralService.getPreferredLanguages(),
          attorneyGeneralService.getRespondentTypes(),
        ]);
      return {
        provinces: prov.success ? prov.data : [],
        urgencyLevels: urg.success ? urg.data : [],
        deliveryMethods: del.success ? del.data : [],
        courtTypes: courtTypes.success ? courtTypes.data : [],
        nextSteps: next.success ? next.data : [],
        countries: countries.success ? countries.data : [],
        genders: genders.success ? genders.data : [],
        suspectStatuses: statuses.success ? statuses.data : [],
        custodyStatuses: custody.success ? custody.data : [],
        nationalities: nationalities.success ? nationalities.data : [],
        preferredMethodOfAccess: access.success ? access.data : [],
        preferredLanguages: languages.success ? languages.data : [],
        respondentTypes: types.success ? types.data : [],
      };
    };

    void load().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!provinceId) {
      setData((d) => ({ ...d, districts: [] }));
      return;
    }

    let mounted = true;
    void attorneyGeneralService.getDistricts(provinceId).then((r) => {
      if (!mounted) return;
      const districts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, districts };
      setData((d) => ({ ...d, districts }));
    });
    return () => {
      mounted = false;
    };
  }, [scope, provinceId]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!districtId) {
      setData((d) => ({ ...d, magistrateCourts: [], highCourts: [], policeStations: [] }));
      return;
    }

    let mounted = true;
    void Promise.all([
      attorneyGeneralService.getMagistrateCourts(districtId, provinceId),
      attorneyGeneralService.getHighCourts(districtId, provinceId),
      attorneyGeneralService.getPoliceStations(districtId),
    ]).then(([mag, high, police]) => {
      if (!mounted) return;
      const magistrateCourts = mag.success ? mag.data : [];
      const highCourts = high.success ? high.data : [];
      const policeStations = police.success ? police.data : [];
      sharedLookups = { ...sharedLookups, magistrateCourts, highCourts, policeStations };
      setData((d) => ({ ...d, magistrateCourts, highCourts, policeStations }));
    });
    return () => {
      mounted = false;
    };
  }, [scope, districtId, provinceId]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    const courtType = courtTypeQueryParam(data.courtTypes, courtTypeId ?? '');
    if (!courtType) {
      setCourtsLoading(false);
      setData((d) => ({ ...d, courts: [] }));
      return;
    }

    let mounted = true;
    setCourtsLoading(true);
    void attorneyGeneralService.getAttorneyGeneralCourts(courtType, districtId, provinceId).then((r) => {
      if (!mounted) return;
      const courts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, courts };
      setData((d) => ({ ...d, courts }));
      setCourtsLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [scope, courtTypeId, districtId, provinceId, data.courtTypes]);

  return {
    loading,
    provinces: data.provinces,
    districts: data.districts,
    magistrateCourts: data.magistrateCourts,
    highCourts: data.highCourts,
    policeStations: data.policeStations,
    countries: data.countries,
    courtTypes: data.courtTypes,
    courts: data.courts,
    courtsLoading,
    urgencyLevels: data.urgencyLevels,
    deliveryMethods: data.deliveryMethods,
    nextSteps: data.nextSteps,
    genders: data.genders,
    suspectStatuses: data.suspectStatuses,
    custodyStatuses: data.custodyStatuses,
    nationalities: data.nationalities,
    preferredMethodOfAccess: data.preferredMethodOfAccess,
    preferredLanguages: data.preferredLanguages,
    respondentTypes: data.respondentTypes,
  };
}
